import React, { useState, useEffect } from 'react';
import { X, Smartphone, Laptop, Share, PlusSquare, Check, Download, Monitor, ShieldCheck, Sparkles, RefreshCw, ExternalLink } from 'lucide-react';

interface PwaHandlerProps {
  isStandalone: boolean;
  canInstall: boolean;
  onInstall: () => void;
  forceShowGuide?: boolean;
  onCloseGuide?: () => void;
  settings?: any;
}

const PwaHandler: React.FC<PwaHandlerProps> = ({ 
  isStandalone, 
  canInstall, 
  onInstall,
  forceShowGuide,
  onCloseGuide,
  settings
}) => {
  const [activeTab, setActiveTab] = useState<'ios' | 'android' | 'desktop'>('android');
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const [hasPrompt, setHasPrompt] = useState<boolean>(() => {
    const p = (window as any).deferredPrompt;
    return !!(p && typeof p.prompt === 'function');
  });
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    const updatePromptStatus = () => {
      const p = (window as any).deferredPrompt;
      setHasPrompt(!!(p && typeof p.prompt === 'function'));
    };

    updatePromptStatus();
    const interval = setInterval(updatePromptStatus, 400);
    window.addEventListener('pwa-prompt-available', updatePromptStatus);
    window.addEventListener('beforeinstallprompt', updatePromptStatus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('pwa-prompt-available', updatePromptStatus);
      window.removeEventListener('beforeinstallprompt', updatePromptStatus);
    };
  }, []);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroidDevice = /android/.test(ua);
    const isMobileUA = /iphone|ipad|ipod|android|blackberry|mini|windows\sphone|palm/i.test(ua);
    const hasTouch = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
    
    setIsMobileDevice(isMobileUA || (hasTouch && window.innerWidth < 1024));

    if (isIosDevice) {
      setActiveTab('ios');
    } else if (isAndroidDevice) {
      setActiveTab('android');
    } else {
      setActiveTab('desktop');
    }
  }, []);

  if (isStandalone) return null;

  const handleClose = () => {
    if (onCloseGuide) {
      onCloseGuide();
    }
  };

  const handleOpenInNewTab = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('action', 'install_pwa');
    window.open(url.toString(), '_blank');
  };

  const handleMainInstallClick = async () => {
    setIsInstalling(true);
    
    // If inside an iframe and no prompt, open in standalone tab
    if (window.self !== window.top && !hasPrompt) {
      handleOpenInNewTab();
      setIsInstalling(false);
      return;
    }

    try {
      await onInstall();
    } finally {
      setTimeout(() => setIsInstalling(false), 1500);
    }
  };

  const getEffectiveBase = () => {
    try {
      const base = (window as any).__DYNAMIC_BASE__;
      if (!base) {
        const path = window.location.pathname.replace(/\/index\.php.*$/, '');
        if (window.location.hostname.includes('andersonferro.com.br') && path.startsWith('/produtos')) {
          return '/produtos';
        }
      }
      return base ? base.replace(/\/+$/, '') : '';
    } catch(e) {
      return '';
    }
  };

  const base = getEffectiveBase();
  const rawIcon = settings?.pwaIconUrl || settings?.logoUrl || '';
  const defaultFallbackIcon = base ? `${base}/icon-192.png` : '/icon-192.png';
  
  const resolveAppIcon = (url: string) => {
    if (!url) return defaultFallbackIcon;
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    if (url.startsWith('/')) return base ? `${base}${url}` : url;
    return base ? `${base}/${url}` : `/${url}`;
  };

  const appIcon = resolveAppIcon(rawIcon);
  const appName = settings?.pwaName || settings?.siteTitle || 'Ecossistema Digital';
  const appDescription = settings?.pwaDescription || settings?.siteDescription || 'Seu ecossistema digital inteligente - Cardápio, Bio Links, Portfólio e Automação de Vendas';

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/85 backdrop-blur-md select-none transition-all duration-300">
      <div 
        onClick={(e) => e.stopPropagation()}
        className="relative bg-[#0b0922] border border-white/10 rounded-[2rem] shadow-[0_30px_100px_rgba(0,0,0,0.9)] w-full max-w-md overflow-hidden flex flex-col text-white transform scale-100 transition-all duration-300 max-h-[92vh] overflow-y-auto"
      >
        {/* Top Colored Accent */}
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-500 shrink-0" />

        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-slate-400 hover:text-white transition-all cursor-pointer z-20"
        >
          <X size={16} />
        </button>

        {/* Header with Verified App Identity */}
        <div className="px-6 pt-6 pb-4 border-b border-white/5 bg-gradient-to-b from-indigo-950/40 to-transparent flex flex-col items-center text-center">
          <div className="relative mb-3">
            <div className="absolute inset-0 rounded-2xl bg-emerald-500/20 blur-lg animate-pulse" />
            <img 
              src={appIcon} 
              alt={appName} 
              className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-contain border border-white/10 shadow-2xl bg-slate-900" 
              referrerPolicy="no-referrer"
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = defaultFallbackIcon; }}
            />
            <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-slate-950 p-1 rounded-full border-2 border-[#0b0922] shadow-lg">
              <ShieldCheck size={14} />
            </div>
          </div>

          <div className="inline-flex items-center gap-1 bg-emerald-500/10 border border-emerald-400/20 px-2.5 py-0.5 rounded-full mb-1.5">
            <Sparkles size={11} className="text-emerald-400" />
            <span className="text-[9px] font-black uppercase tracking-wider text-emerald-300">Aplicativo Oficial Nativo</span>
          </div>

          <h2 className="text-lg sm:text-xl font-black text-white tracking-tight leading-tight">{appName}</h2>
          <p className="text-[11px] text-slate-400 font-medium leading-relaxed max-w-xs mt-1">
            {appDescription}
          </p>

          {/* MAIN PROMINENT INSTALL BUTTON */}
          <div className="w-full mt-4">
            <button
              onClick={handleMainInstallClick}
              disabled={isInstalling}
              className="w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 hover:from-emerald-600 hover:to-teal-700 text-slate-950 font-black text-xs uppercase tracking-wider py-3.5 px-6 rounded-2xl cursor-pointer transition-all shadow-[0_10px_30px_rgba(16,185,129,0.35)] hover:shadow-[0_15px_35px_rgba(16,185,129,0.45)] active:scale-95 text-center flex items-center justify-center gap-2 hover:scale-[1.02] duration-200"
            >
              {isInstalling ? (
                <>
                  <RefreshCw size={15} className="animate-spin" /> ACIONANDO INSTALAÇÃO...
                </>
              ) : (
                <>
                  <Download size={16} className="animate-bounce" /> INSTALAR APLICATIVO NATIVO
                </>
              )}
            </button>
          </div>
        </div>

        {/* Platform Tabs */}
        <div className="px-6 pt-4">
          <div className="bg-black/45 p-1 rounded-xl border border-white/5 grid grid-cols-3 gap-1">
            <button
              onClick={() => setActiveTab('android')}
              className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all cursor-pointer ${
                activeTab === 'android' 
                  ? 'bg-emerald-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Smartphone size={11} /> ANDROID
            </button>
            <button
              onClick={() => setActiveTab('ios')}
              className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all cursor-pointer ${
                activeTab === 'ios' 
                  ? 'bg-emerald-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Smartphone size={11} /> IPHONE
            </button>
            <button
              onClick={() => setActiveTab('desktop')}
              className={`py-2 rounded-lg text-[9px] font-black uppercase tracking-wider flex items-center justify-center gap-1 transition-all cursor-pointer ${
                activeTab === 'desktop' 
                  ? 'bg-emerald-600 text-white shadow-lg' 
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <Monitor size={11} /> DESKTOP
            </button>
          </div>
        </div>

        {/* Tab Specific Content */}
        <div className="p-6 space-y-4 flex-1">
          {/* ANDROID TAB */}
          {activeTab === 'android' && (
            <div className="space-y-3.5 text-left">
              <div className="bg-gradient-to-br from-slate-900/90 to-[#0b0922] border border-emerald-500/20 p-4 rounded-2xl space-y-3 shadow-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm">🤖</span>
                    <p className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Instalação no Android (Chrome):</p>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-slate-200">
                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">1</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Clique no botão verde <strong>"INSTALAR APLICATIVO NATIVO"</strong> acima.
                    </p>
                  </div>

                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">2</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Se o seu Chrome pedir confirmação, toque em <strong>"Instalar"</strong>.
                    </p>
                  </div>

                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">3</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Caso prefira, toque nos <strong>3 Pontos (⋮)</strong> do Chrome e escolha <strong>"Instalar aplicativo"</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {window.self !== window.top && (
                <button
                  onClick={handleOpenInNewTab}
                  className="w-full inline-flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 text-emerald-300 hover:text-emerald-200 font-bold text-[10px] uppercase tracking-wider py-2.5 px-3 rounded-xl cursor-pointer transition-all border border-emerald-500/20"
                >
                  <ExternalLink size={12} /> Abrir em Tela Cheia para Instalar Diretamente
                </button>
              )}
            </div>
          )}

          {/* IPHONE / IOS TAB */}
          {activeTab === 'ios' && (
            <div className="space-y-3.5 text-left">
              <div className="bg-gradient-to-br from-slate-900/90 to-[#0b0922] border border-emerald-500/20 p-4 rounded-2xl space-y-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm">🍎</span>
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Como instalar no iPhone / iPad (Safari):</p>
                </div>

                <div className="space-y-2 text-xs text-slate-200">
                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">1</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Toque no ícone <Share size={12} className="inline text-emerald-400 mx-1" /> <strong>Compartilhar</strong> na barra inferior do Safari.
                    </p>
                  </div>

                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">2</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Role e toque em <PlusSquare size={12} className="inline text-emerald-400 mx-1" /> <strong>"Adicionar à Tela de Início"</strong>.
                    </p>
                  </div>

                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">3</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Toque em <strong>"Adicionar"</strong> no canto superior direito para finalizar!
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* DESKTOP TAB */}
          {activeTab === 'desktop' && (
            <div className="space-y-3.5 text-left">
              <div className="bg-gradient-to-br from-slate-900/90 to-[#0b0922] border border-emerald-500/20 p-4 rounded-2xl space-y-3 shadow-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm">💻</span>
                  <p className="text-[10px] font-black uppercase tracking-wider text-emerald-300">Instalação no Computador (Chrome / Edge):</p>
                </div>

                <div className="space-y-2 text-xs text-slate-200">
                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">1</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Clique no botão <strong>"INSTALAR APLICATIVO NATIVO"</strong> acima.
                    </p>
                  </div>

                  <div className="flex gap-2.5 items-center bg-black/40 p-2.5 rounded-xl border border-white/5">
                    <div className="w-5 h-5 rounded-md bg-emerald-500/20 text-emerald-300 font-black text-[11px] flex items-center justify-center shrink-0">2</div>
                    <p className="text-[11px] text-slate-200 leading-tight">
                      Ou clique no ícone de <strong>"+"</strong> ou monitor no canto direito da barra de endereços do navegador.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Benefits Strip */}
        <div className="px-6 pb-2">
          <div className="bg-white/5 border border-white/5 p-3 rounded-xl flex items-center justify-around text-center">
            <div className="flex items-center gap-1.5">
              <Check size={13} className="text-emerald-400" />
              <span className="text-[10px] font-bold text-slate-300">Tela Cheia</span>
            </div>
            <div className="h-3 w-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <Check size={13} className="text-emerald-400" />
              <span className="text-[10px] font-bold text-slate-300">Acesso Rápido</span>
            </div>
            <div className="h-3 w-px bg-white/10" />
            <div className="flex items-center gap-1.5">
              <Check size={13} className="text-emerald-400" />
              <span className="text-[10px] font-bold text-slate-300">Notificações</span>
            </div>
          </div>
        </div>

        {/* Footer Close Area */}
        <div className="bg-[#08061a] p-4 border-t border-white/5 flex justify-end">
          <button 
            onClick={handleClose}
            className="w-full bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white border border-white/10 font-bold text-[10px] uppercase tracking-wider py-3 px-6 rounded-xl cursor-pointer transition-all active:scale-95 text-center"
          >
            Fechar e Voltar ao Site
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaHandler;
